/*
 * File: UpdatePeriodEnrollmentPrivateNotes.ts
 * Author: Austin Robinson
 * Date Created: 11/4/2025
 * Copyright (c) DrivingDoc 2025
 */
import { createMutation } from 'react-query-kit';
import type { SerializedEditorState } from 'lexical';

import { privateApi } from 'api';
import { invalidateQueryKeys } from 'api/hooks/middlewares';

export type UpdatePeriodEnrollmentPrivateNotesParams = {
  studentId: string;
  periodId: string;
  privateNotes: SerializedEditorState;
};

export type UpdatePeriodEnrollmentPrivateNotesResult = {
  student_user_id: string;
  course_period_id: string;
  private_notes: SerializedEditorState;
  private_notes_updated_at: string;
  private_notes_updated_by: string;
};

async function updatePeriodEnrollmentPrivateNotes(
  params: UpdatePeriodEnrollmentPrivateNotesParams,
): Promise<UpdatePeriodEnrollmentPrivateNotesResult> {
  // make api call
  const result = await privateApi.patch(
    `${import.meta.env.VITE_API_BASE_URL}/courses/periods/enrollments/private-notes`,
    params,
  );

  // return the data object
  return result.data;
}

const useUpdatePeriodEnrollmentPrivateNotes = createMutation({
  mutationFn: async (variables: UpdatePeriodEnrollmentPrivateNotesParams) =>
    updatePeriodEnrollmentPrivateNotes(variables),
  use: [
    invalidateQueryKeys<UpdatePeriodEnrollmentPrivateNotesResult, UpdatePeriodEnrollmentPrivateNotesParams>(
      (_data, variables) => [
        // Invalidate the specific course period to update the Drive Overview Dialog
        { queryKey: ['course_period', { id: variables.periodId }] },
        // Invalidate all course periods to update period lists and schedules
        { queryKey: ['course_periods'] },
        // Invalidate student's period enrollments to update Drive History section
        { queryKey: ['student_course_periods', { id: variables.studentId }] },
      ],
    ),
  ],
});

export { updatePeriodEnrollmentPrivateNotes, useUpdatePeriodEnrollmentPrivateNotes };
